import React, { useState, useEffect } from 'react';
import { RotateCcw, CheckCircle, XCircle, Clock, User, Calendar, Car, Bike } from 'lucide-react';
import { toast } from 'sonner';
import { getEventReEntryRegistrations, approveReEntryRegistration, cancelReEntryRegistration } from '../../lib/reEntryUtils';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { formatCurrency } from '../../lib/currencyUtils';

interface ReEntryManagementProps {
  eventId: string;
}

export default function ReEntryManagement({ eventId }: ReEntryManagementProps) {
  const { user } = useAuth();
  const [reEntryRegistrations, setReEntryRegistrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<any | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchReEntryRegistrations();
  }, [eventId]);

  const fetchReEntryRegistrations = async () => {
    setLoading(true);
    try {
      const registrations = await getEventReEntryRegistrations(eventId);
      setReEntryRegistrations(registrations);
    } catch (error) {
      console.error('Error fetching re-entry registrations:', error);
      toast.error('Failed to load re-entry registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (registration: any) => {
    if (!user?.id) return;

    setIsProcessing(true);
    try {
      await approveReEntryRegistration(registration.id, user.id);
      toast.success('Re-entry registration approved successfully!');
      await fetchReEntryRegistrations();
    } catch (error) {
      console.error('Error approving re-entry registration:', error);
      toast.error('Failed to approve re-entry registration');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedRegistration || !user?.id) return;

    setIsProcessing(true);
    try {
      await cancelReEntryRegistration(selectedRegistration.id, user.id);
      toast.success('Re-entry registration cancelled');
      setShowCancelModal(false);
      setSelectedRegistration(null);
      await fetchReEntryRegistrations();
    } catch (error) {
      console.error('Error cancelling re-entry registration:', error);
      toast.error('Failed to cancel re-entry registration');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4 text-yellow-400" />;
      case 'approved': return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-400" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVehicleIcon = (vehicleType: string) => {
    return vehicleType === 'car' ? <Car className="h-4 w-4" /> : <Bike className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-white">Loading re-entry requests...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center">
          <RotateCcw className="h-5 w-5 mr-2" />
          Re-entry Registrations
        </h3>
        <button
          onClick={fetchReEntryRegistrations}
          className="px-3 py-1 bg-zinc-700 text-white text-sm rounded hover:bg-zinc-600"
        >
          Refresh
        </button>
      </div>

      {reEntryRegistrations.length === 0 ? (
        <div className="text-center py-8">
          <RotateCcw className="h-12 w-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">
            No Re-entry Registrations
          </h3>
          <p className="text-gray-400">
            No re-entry registrations have been submitted for this event.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reEntryRegistrations.map((registration) => (
            <div key={registration.id} className="bg-zinc-800 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(registration.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(registration.status)}`}>
                        {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <User className="h-4 w-4" />
                      <span className="text-sm">{registration.users?.full_name || 'Unknown Driver'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm">{formatDateForDisplay(registration.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-300 mb-2">
                    <div className="flex items-center gap-1">
                      {getVehicleIcon(registration.event_categories?.vehicle_type || 'car')}
                      <span>{registration.event_categories?.name || 'Unknown Category'}</span>
                    </div>
                    <span>•</span>
                    <span>{registration.users?.email || 'No email'}</span>
                  </div>



                  <div className="text-sm text-gray-400 mb-2">
                    <strong>Payment Status:</strong> {registration.payment_status}
                  </div>

                  <div className="text-sm text-orange-400 mb-2">
                    <strong>Re-entry Fee:</strong> {formatCurrency(registration.re_entry_fee || 0)}
                  </div>

                  {registration.status === 'completed' && (
                    <div className="text-sm text-green-400">
                      <strong>Ready to Compete</strong>
                    </div>
                  )}

                  {registration.amount_paid > 0 && (
                    <div className="text-sm text-primary-400">
                      <strong>Amount Paid:</strong> {formatCurrency(registration.amount_paid)}
                    </div>
                  )}
                </div>

                {registration.status === 'pending' && (
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleApprove(registration)}
                      disabled={isProcessing}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      <CheckCircle className="h-4 w-4 inline mr-1" />
                      Approve & Mark Paid
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRegistration(registration);
                        setShowCancelModal(true);
                      }}
                      disabled={isProcessing}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      <XCircle className="h-4 w-4 inline mr-1" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cancellation Modal */}
      {showCancelModal && selectedRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Cancel Re-entry Registration</h3>
            
                          <div className="space-y-4">
                <div>
                  <p className="text-gray-300 mb-2">
                    Cancelling re-entry registration for <span className="text-white font-medium">
                      {selectedRegistration.users?.full_name || 'Unknown Driver'}
                    </span>
                  </p>
                  <p className="text-sm text-gray-400">
                    This will cancel the re-entry and allow the driver to request re-entry again.
                  </p>
                </div>
              </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedRegistration(null);
                }}
                className="px-4 py-2 text-gray-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleCancel}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Cancelling...' : 'Cancel Registration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 